import React, { useState, useEffect } from 'react'

import './style/Audio.scss'

const Audio: React.FC<{ onRecord: (file: File) => void }> = ({ onRecord }) => {
  const [shouldSend, setShouldSend] = useState(false)
  const [isDeviceAvailable, setDeviceAvailable] = useState(false)

  // Stream & Record
  const [stream, setStream] = useState<MediaStream>()
  const [audioChunks, setAudioChunks] = useState<Blob[]>([])
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>()

  // Timer
  const [seconds, setSeconds] = useState(0)
  const minutes = Math.floor(seconds / 60)

  async function handleStart (): Promise<void> {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const constraints = {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 44100
          }
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        setStream(stream)

        // Set codec options based on browser support
        const options = {
          mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : MediaRecorder.isTypeSupported('audio/mp4')
              ? 'audio/mp4'
              : 'audio/wav',
          audioBitsPerSecond: 128000
        }

        console.log('Using audio format:', options.mimeType)
        const recorder = new MediaRecorder(stream, options)
        setMediaRecorder(recorder)
        setAudioChunks([])
      }
    } catch (error) {
      console.error('Error starting recording:', error)
      alert('Erro ao iniciar gravação. Verifique as permissões do microfone.')
    }
  }

  async function handleCancel (): Promise<void> {
    if (stream && mediaRecorder) {
      // Stop Stream
      const tracks = stream.getTracks()
      tracks.forEach(track => track.stop())

      // Stop Record
      mediaRecorder.stop()

      // Clean
      setStream(undefined)
      setMediaRecorder(undefined)

      // Timer
      setSeconds(0)

      // Prevent send
      setShouldSend(false)
    }
  }

  async function handleSend (): Promise<void> {
    handleCancel() // Stop, clean and store blob
    setShouldSend(true)
  }

  // Send file
  useEffect(() => {
    if (shouldSend && audioChunks.length) {
      console.log('Creating blob from chunks:', audioChunks.length)

      const mimeType = mediaRecorder?.mimeType || 'audio/wav'
      const blob = new Blob(audioChunks, { type: mimeType })

      if (blob.size === 0) {
        alert('Erro na gravação: arquivo de áudio vazio')
        return
      }

      const extension = mimeType.includes('webm')
        ? 'webm'
        : (mimeType.includes('mp4') ? 'm4a' : 'wav')
      const file = new File([blob], `audio.${extension}`, {
        type: mimeType,
        lastModified: Date.now()
      })

      onRecord(file)
    }
  }, [shouldSend, audioChunks, mediaRecorder?.mimeType])

  // Handle record start and stop
  useEffect(() => {
    if (stream && mediaRecorder) {
      // Get data every 1 second for better mobile compatibility
      mediaRecorder.start(1000)
      console.log('MediaRecorder started:', mediaRecorder.state)

      const chunks: Blob[] = []
      const interval = setInterval(() => {
        setSeconds(seconds => (seconds + 1))
      }, 1000)

      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) {
          console.log('Received chunk of size:', e.data.size)
          chunks.push(e.data)
        } else {
          console.warn('Received empty chunk')
        }

        if (mediaRecorder.state === 'inactive') {
          console.log('Recording finished, total chunks:', chunks.length)
          clearInterval(interval)
          setAudioChunks(chunks)
        }
      }

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
      }
    }
  }, [stream, mediaRecorder])

  useEffect(() => {
    setDeviceAvailable(!!navigator.mediaDevices)
  }, [])

  return (
    <>
      {
        stream
          ? (
            <span
              className="secondary button margin-right-8 audio-record-button recording"
              style={{ borderRadius: 25, height: 50, width: 200 }}
            >
              <i className="fa fa-times-circle" onClick={handleCancel} />
              <span className="record-signal blinking" /><i className="fas fa-microphone" />&nbsp;
              { minutes < 10 ? `0${minutes}` : minutes}:{ seconds < 10 ? `0${seconds}` : seconds }
              <i className="fa fa-check-circle" onClick={handleSend} />
            </span>

          )
          : (
            isDeviceAvailable
              ? (
                <span
                  onClick={handleStart}
                  title="Clique para gravar um áudio"
                  className="secondary button margin-right-8 audio-record-button"
                  style={{ borderRadius: '50%', height: 50, width: 50 }}
                >
                  <i className="fas fa-microphone" />
                </span>
              )
              : (
                <span
                  onClick={() => alert('Microfone não habilitado')}
                  className="secondary button margin-right-8 audio-record-button"
                  style={{ borderRadius: '50%', height: 50, width: 50, opacity: 0.4, cursor: 'not-allowed' }}
                >
                  <i className="fas fa-microphone" />
                </span>
              )
          )
      }
    </>
  )
}

export default Audio
